import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AdminBlogPost } from '@app/model/blog-management.model';
import { BlogManagementDataService } from '@app/services/blog-management-data.service';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material';
import { BlogPostCreateComponent } from '../blog-post-create/blog-post-create.component';
import { BlogPostEditComponent } from '../blog-post-edit/blog-post-edit.component';
import { BlogPostPreviewComponent } from '../blog-post-preview/blog-post-preview.component';
import { LayoutUtilsService } from '@app/core/_base/crud';
import { ToastrService } from 'ngx-toastr';

interface BlogCategoryFilter {
  id: string;
  name: string;
}

@Component({
  selector: 'meu-blog-management',
  templateUrl: './blog-management.component.html',
  styleUrls: ['./blog-management.component.scss']
})
export class BlogManagementComponent implements OnInit, OnDestroy {
  section = '';
  title = '';

  posts: AdminBlogPost[] = [];
  filteredPosts: AdminBlogPost[] = [];
  categories: BlogCategoryFilter[] = [];

  displayedColumns = [
    'Index',
    'Image',
    'Post',
    'Category',
    'Author',
    'ViewCount',
    'Status',
    'Type',
    'PublishedAt',
    'Actions'
  ];

  searchText = '';
  selectedCategoryId = '';
  selectedPublishStatus = 'all';
  selectedPostType = 'all';

  @ViewChild('pageNumberInput', { static: false })
  pageNumberInput!: ElementRef<HTMLInputElement>;

  pageSize = 10;
  pageIndex = 1;
  isEditingPage = false;

  isLoading = false;
  loadError = false;
  deletingPostId = '';

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private blogManagementDataService: BlogManagementDataService,
    private dialog: MatDialog,
    private layoutUtilsService: LayoutUtilsService,
    private toastr: ToastrService
  ) {}

  get totalPages(): number {
    return Math.ceil(this.filteredPosts.length / this.pageSize);
  }

  get visiblePages(): number[] {
    const maxVisiblePages = 5;

    let startPage = Math.max(1, this.pageIndex - Math.floor(maxVisiblePages / 2));

    const endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);

    startPage = Math.max(1, endPage - maxVisiblePages + 1);

    return Array.from(
      {
        length: Math.max(0, endPage - startPage + 1)
      },
      (_, index) => startPage + index
    );
  }

  ngOnInit() {
    this.section = this.route.snapshot.data.section;
    this.title = this.route.snapshot.data.title;

    if (this.section === 'posts') {
      this.loadPosts();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPosts() {
    this.isLoading = true;
    this.loadError = false;

    this.blogManagementDataService
      .getAllPosts()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(
        (posts) => {
          this.posts = posts;
          this.categories = this.createCategoryFilters(posts);
          this.applyFilters();
          this.isLoading = false;
        },
        () => {
          this.posts = [];
          this.filteredPosts = [];
          this.isLoading = false;
          this.loadError = true;
        }
      );
  }

  applyFilters() {
    const searchValue = this.searchText.trim().toLowerCase();

    this.filteredPosts = this.posts.filter((post) => {
      const matchesSearch =
        !searchValue ||
        post.title.toLowerCase().includes(searchValue) ||
        post.slug.toLowerCase().includes(searchValue);

      const matchesCategory =
        !this.selectedCategoryId || post.categoryId === this.selectedCategoryId;

      const matchesPublishStatus =
        this.selectedPublishStatus === 'all' ||
        (this.selectedPublishStatus === 'published' && post.isPublished) ||
        (this.selectedPublishStatus === 'unpublished' && !post.isPublished);

      const matchesPostType =
        this.selectedPostType === 'all' ||
        (this.selectedPostType === 'hot' && post.isHot) ||
        (this.selectedPostType === 'featured' && post.isFeatured) ||
        (this.selectedPostType === 'regular' && !post.isHot && !post.isFeatured);

      return matchesSearch && matchesCategory && matchesPublishStatus && matchesPostType;
    });

    this.pageIndex = 1;
    this.isEditingPage = false;
  }

  clearFilters() {
    this.searchText = '';
    this.selectedCategoryId = '';
    this.selectedPublishStatus = 'all';
    this.selectedPostType = 'all';

    this.applyFilters();
  }

  goToPage(page: number | string) {
    const parsedPage = Number(page);

    if (isNaN(parsedPage)) {
      this.cancelPageEdit();
      return;
    }

    const lastPage = this.totalPages || 1;

    this.pageIndex = Math.min(Math.max(Math.floor(parsedPage), 1), lastPage);

    this.isEditingPage = false;
  }

  startPageEdit() {
    this.isEditingPage = true;

    setTimeout(() => {
      if (!this.pageNumberInput) {
        return;
      }

      this.pageNumberInput.nativeElement.focus();
      this.pageNumberInput.nativeElement.select();
    });
  }

  cancelPageEdit() {
    this.isEditingPage = false;
  }

  openCreatePost() {
    const dialogRef = this.dialog.open(BlogPostCreateComponent, {
      width: '1600px',
      maxWidth: '98vw',
      maxHeight: '94vh',
      panelClass: 'blog-post-create-dialog',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.created) {
        this.loadPosts();
      }
    });
  }
  openEditPost(post: AdminBlogPost) {
    const dialogRef = this.dialog.open(BlogPostEditComponent, {
      width: '1600px',
      maxWidth: '98vw',
      maxHeight: '94vh',
      panelClass: 'blog-post-edit-dialog',
      disableClose: true,
      data: {
        mode: 'edit',
        post
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.updated) {
        this.loadPosts();
      }
    });
  }
  openPreviewPost(post: AdminBlogPost) {
    this.dialog.open(BlogPostPreviewComponent, {
      width: '1240px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      panelClass: 'blog-post-preview-dialog',
      data: {
        post
      }
    });
  }

  confirmDeletePost(post: AdminBlogPost) {
    const title = 'Xóa bài viết';
    const description = 'Bạn có chắc chắn muốn xóa bài viết "' + post.title + '" không?';
    const waitDescription = 'Đang xóa bài viết...';

    const dialogRef = this.layoutUtilsService.deleteElement(title, description, waitDescription);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.deletePost(post);
      });
  }

  private deletePost(post: AdminBlogPost) {
    this.deletingPostId = post.id;

    this.blogManagementDataService
      .deletePost(post.id)
      .pipe(
        take(1),
        finalize(() => {
          this.deletingPostId = '';
        })
      )
      .subscribe(
        (response) => {
          if (!response || !response.success) {
            this.toastr.error(
              response && response.message ? response.message : 'Không thể xóa bài viết'
            );

            return;
          }

          this.toastr.success('Xóa bài viết thành công');
          this.loadPosts();
        },
        (error) => {
          console.error('Delete blog post failed:', error);

          const errorResponse = error && error.error ? error.error : null;

          let message = 'Không thể xóa bài viết';

          if (errorResponse && errorResponse.message) {
            message = errorResponse.message;
          } else if (errorResponse && errorResponse.title) {
            message = errorResponse.title;
          }

          this.toastr.error(message);
        }
      );
  }

  private createCategoryFilters(posts: AdminBlogPost[]): BlogCategoryFilter[] {
    const categories = posts.map((post) => ({
      id: post.categoryId,
      name: post.categoryName || 'Chưa xác định'
    }));

    return categories
      .filter(
        (category, index, items) => index === items.findIndex((item) => item.id === category.id)
      )
      .sort((firstCategory, secondCategory) =>
        firstCategory.name.localeCompare(secondCategory.name)
      );
  }
}
